import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State = "loading" | "valid" | "already" | "invalid" | "success" | "error";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { setState("invalid"); return; }
        setEmail(data.email ?? null);
        if (data.already_unsubscribed) setState("already");
        else setState("valid");
      } catch {
        setState("error");
      }
    })();
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    const { error } = await supabase.functions.invoke("handle-email-unsubscribe", { body: { token } });
    setSubmitting(false);
    setState(error ? "error" : "success");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Unsubscribe</CardTitle>
          <CardDescription>Manage email preferences for Sortable</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "loading" && (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Validating link…</div>
          )}
          {state === "valid" && (
            <>
              <p className="text-sm">Unsubscribe {email ?? "this address"} from non-essential Sortable emails?</p>
              <Button onClick={confirm} disabled={submitting} className="w-full">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm unsubscribe"}
              </Button>
            </>
          )}
          {state === "already" && (
            <div className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" /><p className="text-sm">{email ?? "This address"} is already unsubscribed.</p></div>
          )}
          {state === "success" && (
            <div className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" /><p className="text-sm">You've been unsubscribed. You may still receive essential account emails.</p></div>
          )}
          {state === "invalid" && (
            <div className="flex items-start gap-2"><XCircle className="h-5 w-5 text-destructive mt-0.5" /><p className="text-sm">This unsubscribe link is invalid or expired.</p></div>
          )}
          {state === "error" && (
            <div className="flex items-start gap-2"><XCircle className="h-5 w-5 text-destructive mt-0.5" /><p className="text-sm">Something went wrong. Please try again later.</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
